import { ABILITIES } from "../content/abilities";
import { CLASSES } from "../content/classes";
import { alliesOf, canCast, foesOf, isActable } from "./query";
import type { PlayerInput, Unit, Wardstone, World } from "./types";
import { dist, norm, sub, vec } from "./vec";

function idle(unit: Unit): PlayerInput {
  return {
    unitId: unit.id,
    moveX: 0,
    moveY: 0,
    aimX: unit.pos.x + Math.cos(unit.facing),
    aimY: unit.pos.y + Math.sin(unit.facing),
    skill: false,
    ult: false,
    clickedId: null,
    clearTarget: false,
  };
}

function wantedStone(world: World, unit: Unit): Wardstone | null {
  const stones = world.suddenDeath ? world.stones.filter((s) => s.name === "Heart") : world.stones;
  let best: Wardstone | null = null;
  let bestScore = -Infinity;
  for (const s of stones) {
    const d = dist(unit.pos, s.pos);
    let value = 2.2 - d * 0.08;
    if (s.owner !== unit.team) value += 3.4;
    if (s.owner === null) value += 1.6;
    if (s.owner === unit.team) value -= 1.2;
    if (s.capturing !== null && s.capturing !== unit.team && s.owner === unit.team) value += 2.5;
    if (world.suddenDeath && s.name === "Heart") value += 6;
    if (value > bestScore) {
      bestScore = value;
      best = s;
    }
  }
  return best;
}

function nearestFoe(world: World, unit: Unit): Unit | null {
  return foesOf(world, unit).reduce<Unit | null>((best, e) => {
    if (e.kind === "corpse") return best;
    if (!best || dist(unit.pos, e.pos) < dist(unit.pos, best.pos)) return e;
    return best;
  }, null);
}

export function aiInput(world: World, unit: Unit): PlayerInput {
  const out = idle(unit);
  if (unit.kind === "spirit") return spiritAi(world, unit, out);
  if (!isActable(unit)) return out;

  const hp = unit.hp / Math.max(1, unit.maxHp);
  const nearest = nearestFoe(world, unit);
  const stone = wantedStone(world, unit);
  const def = CLASSES[unit.classId];
  const auto = ABILITIES[def.autos[unit.state.weapon ?? 0] ?? def.autos[0]];
  const skill = def.skill ? ABILITIES[def.skill] : undefined;
  const ult = def.ult ? ABILITIES[def.ult] : undefined;
  const retreat = hp < 0.28 && unit.kind === "hero";

  let dest = unit.spawn;
  let aim = nearest ? nearest.pos : vec(unit.pos.x + (unit.team === 0 ? 2 : -2), unit.pos.y);

  if (retreat) {
    dest = unit.spawn;
    if (skill && skill.kind === "dash" && unit.skillCd <= 0) out.skill = true;
  } else {
    const hurtAlly = alliesOf(world, unit, true).find((a) => a.kind === "hero" && (a.hp / a.maxHp < 0.65 || a.statuses.some((s) => s.id === "bleeding" || s.id === "poison")));
    if (hurtAlly && skill && (skill.target === "ally" || skill.target === "any" || skill.id === "rally" || skill.id === "sanctuary" || skill.id === "be_not_afraid" || skill.id === "life_transfer")) {
      if (unit.skillCd <= 0 && dist(unit.pos, hurtAlly.pos) < (skill.range || 5) + 1) {
        out.skill = true;
        aim = hurtAlly.pos;
      }
    }

    const fight = nearest && dist(unit.pos, nearest.pos) < 8;
    if (stone && stone.owner !== unit.team && (!fight || hp < 0.5)) {
      dest = stone.pos;
    } else if (nearest && auto) {
      const d = dist(unit.pos, nearest.pos);
      const ideal = auto.kind === "meleeArc" || auto.kind === "custom" && auto.range < 3 ? auto.range * 0.72 : auto.range * 0.78;
      if (d > ideal) dest = nearest.pos;
      else if (d < ideal * 0.5 && auto.range > 3) dest = vec(unit.pos.x * 2 - nearest.pos.x, unit.pos.y * 2 - nearest.pos.y);
      else if (stone && stone.owner !== unit.team) dest = stone.pos;
      else dest = nearest.pos;
      aim = nearest.pos;
    } else if (stone) {
      dest = stone.pos;
    }

    if (nearest && skill && unit.skillCd <= 0 && canCast(unit)) {
      const range = skill.range || skill.radius || 3;
      if (skill.kind === "dash" && hp < 0.45) out.skill = true;
      else if (skill.id === "close_quarters") {
        const wantSword = dist(unit.pos, nearest.pos) < 2;
        if (wantSword !== ((unit.state.weapon ?? 0) === 1)) out.skill = true;
      } else if (skill.target === "foe" || skill.target === "any") {
        if (dist(unit.pos, nearest.pos) <= range + 0.6) out.skill = true;
      } else if (skill.target === "none") {
        if (dist(unit.pos, nearest.pos) < 5) out.skill = true;
      }
    }

    if (nearest && ult && unit.ultCd <= 0 && canCast(unit)) {
      const cluster = foesOf(world, unit).filter((f) => dist(f.pos, nearest.pos) < 3).length;
      if (cluster >= 2 || hp < 0.4 || nearest.hp <= 4) out.ult = true;
    }

    if (nearest) aim = nearest.pos;
  }

  if (unit.casting || unit.charging) dest = unit.pos;

  const dir = norm(sub(dest, unit.pos));
  if (dist(unit.pos, dest) < 0.35) {
    out.moveX = 0;
    out.moveY = 0;
  } else {
    out.moveX = dir.x;
    out.moveY = dir.y;
  }
  out.aimX = aim.x;
  out.aimY = aim.y;
  return out;
}

function spiritAi(world: World, unit: Unit, out: PlayerInput): PlayerInput {
  let best: Unit | null = null;
  let bestD = Infinity;
  for (const u of world.units) {
    if (u.id === unit.id || u.dead || u.kind === "spirit" || u.kind === "corpse") continue;
    const d = dist(unit.pos, u.pos);
    if (d < bestD) {
      best = u;
      bestD = d;
    }
  }
  if (best && bestD > 1.2) {
    const dir = norm(sub(best.pos, unit.pos));
    out.moveX = dir.x;
    out.moveY = dir.y;
    out.aimX = best.pos.x;
    out.aimY = best.pos.y;
  }
  return out;
}
